import { useMutation, useQuery } from '@apollo/client';
import { StarBorderOutlined } from '@mui/icons-material';
import { Widget, WidgetContent, WidgetHeader, WidgetIcon } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataGridContextProvider } from '../../components/DataGridCustom';
import { TsrctDemandAwardInput } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { useConfig } from '~/components/ConfigProvider/ConfigProvider.context';
import { useModuleContext } from '~/components/ModuleContextProvider';

import { ActionCellContext } from './cells/ActionCell/ActionCell';
import { AwardingDataGrid } from './components/AwardingDataGrid';
import { DEMAND_AWARDING_QUERY } from './DemandAwardingQuery.gql';
import { DEMAND_AWARD_NEGOTIATION_CASE } from './DemandAwardNegotiationCase.gql';

export const DemandAwardingWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'DemandAwardingWidget' });
  const { features } = useConfig();

  const { data, loading } = useQuery(DEMAND_AWARDING_QUERY, {
    variables: {
      id: entityId,
    },
    skip: !entityId,
  });

  const [awardNegotiationCase] = useMutation(DEMAND_AWARD_NEGOTIATION_CASE, {
    refetchQueries: [
      {
        query: DEMAND_AWARDING_QUERY,
        variables: {
          id: entityId,
        },
      },
    ],
  });

  const contextValue: ActionCellContext = useMemo(
    () => ({
      onAwardClick: async (data) => {
        const input: TsrctDemandAwardInput = {
          negotiationCaseId: data.negotiationCaseId,
        };
        if (data.informAwardedSupplier) {
          input['awardedSupplier'] = {
            informSupplier: data.informAwardedSupplier,
            message: data.awardedSupplierMessage,
          };
          input['dismissedSupplier'] = {
            informSupplier: data.informDismissedSuppliers ?? false,
          };
        }
        await awardNegotiationCase({
          variables: {
            id: entityId,
            data: input,
          },
        });
      },
    }),
    [entityId, awardNegotiationCase],
  );
  return (
    <Widget hover={false}>
      <WidgetHeader title={t('title')} icon={<WidgetIcon icon={<StarBorderOutlined fontSize="small" />} />} />
      <WidgetContent disablePadding>
        <DataGridContextProvider value={contextValue}>
          <AwardingDataGrid
            isMaskedBidding={data?.tesseract.demand.competitiveBiddingRule.isMaskedBidding}
            negotiationCases={data?.tesseract.demand.negotiationCases.nodes}
            awardingSettings={data?.tesseract.demand.awardingSettings}
            status={data?.tesseract.demand.awardingStatus}
            awardableConfirmedQuotes={features.awardableConfirmedQuotes}
            loading={loading}
          />
        </DataGridContextProvider>
      </WidgetContent>
    </Widget>
  );
};
